import { Box, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { getActiveAppContext } from "app/tools/common"
import { CONFIGURATION_CHANGE_LABEL_KEY, IForm, IFormChange } from "form/model"
import { getFormChangesCount } from "form/tools/change"
import { getForm } from "form/tools/common"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"

interface IChangeDetailsProps {
  sx?: any
}

interface IChangeDetailsConnectProps {
  form: IForm | null
}

interface IChangeDetailProps {
  change: IFormChange
}

interface IChangeDetailRowProps {
  type: "add" | "update" | "delete"
  ids: string[]
  showIds: boolean
}

const HorizontalDivider = () => (
  <Box
    sx={{ width: 0, borderRight: theme => `1px solid ${theme.palette.text.secondary}`, height: "1.3rem", ml: ".6rem" }}
  />
)

const ChangeDetailRow = React.memo(({ type, ids, showIds }: IChangeDetailRowProps) => (
  <FlexBox ai="start" jc="start" sx={{ ml: ".6rem" }}>
    <Typography color="textPrimary" sx={{ fontSize: ".85rem", minWidth: showIds ? "5rem" : undefined }}>
      {translate(`form.change.type.${type}`)}
    </Typography>
    <FlexBox sx={{ ml: ".6rem" }}>
      {!showIds ? (
        <Typography color="textSecondary" sx={{ fontSize: ".8rem" }}>
          {ids.length}
        </Typography>
      ) : (
        <Typography color="textSecondary" sx={{ fontSize: ".8rem" }}>
          {ids.join(", ")}
        </Typography>
      )}
    </FlexBox>
  </FlexBox>
))

const ChangeDetail = React.memo(({ change }: IChangeDetailProps) => {
  const showIds = change.labelKey === CONFIGURATION_CHANGE_LABEL_KEY
  const displayAdd = change.addedItems.length > 0
  const displayUpdate = change.updatedItems.length > 0
  const displayDelete = change.deletedItems.length > 0
  return (
    <FlexBox ai="start" jc="start" sx={{ mt: ".8rem" }}>
      <Typography color="textPrimary" sx={{ fontSize: ".9rem", width: "8rem", minWidth: "8rem", maxWidth: "8rem" }}>
        {translate(change.labelKey)}
      </Typography>
      {displayAdd && <ChangeDetailRow type="add" ids={change.addedItems} showIds={showIds} />}
      {displayUpdate && (
        <>
          {displayAdd && <HorizontalDivider />}
          <ChangeDetailRow type="update" ids={change.updatedItems} showIds={showIds} />
        </>
      )}
      {displayDelete && (
        <>
          {(displayAdd || displayUpdate) && <HorizontalDivider />}
          <ChangeDetailRow type="delete" ids={change.deletedItems} showIds={showIds} />
        </>
      )}
    </FlexBox>
  )
})

const ChangeDetailsFn = React.memo(({ form, sx }: IChangeDetailsProps & IChangeDetailsConnectProps) => {
  if (!form) {
    return null
  }
  const count: number = getFormChangesCount(form.changes)
  const allChanges = [
    ...form.changes.configuration,
    ...form.changes.geoTargets,
    ...form.changes.screens,
    ...form.changes.audience,
    ...form.changes.creative
  ].filter(c => c.changeCount > 0)
  return (
    <FlexBox ai="start" jc="start" vertical sx={{ ...sx }}>
      {count === 0 ? (
        <Typography color="textPrimary" sx={{ fontSize: ".8rem" }}>
          {translate("label.no.change")}
        </Typography>
      ) : (
        allChanges.map(c => <ChangeDetail change={c} key={c.labelKey} />)
      )}
    </FlexBox>
  )
})

export const ChangeDetails = connect(
  (state: IRootState): IChangeDetailsConnectProps => ({
    form: getActiveAppContext(state) ? getForm(state) : null
  })
)(ChangeDetailsFn)
